import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { Link } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from '../../redux';
import { resetReport, resetTracks, loadTracks, loadUsers, loadProjects, loadClients, getReport } from '../../redux';
import { ChangesList, Intervalpicker, ClientSelectField, Loader, Button, Icon, Select } from '../';
import { Tabs } from 'flowbite-react';
import { Change, Client, User, Project, Sprint, Goal } from '../../models';
import { Tracks } from './Tracks';
import { Dashboard, DashboardSkeleton } from './Dashboard';
import type { ReportingContext, WorkFilter } from './Dashboard';
import moment from 'moment';
import type { Moment } from 'moment';
import type { Interval } from '../../components';
import { clientPath } from '../../utils/clientPath';

type ReportingProps = {
    user?: User;
    project?: Project;
    client?: Client;
    sprint?: Sprint;
    goal?: Goal;
    from?: Moment;
    to?: Moment;
    tabs?: string[];
    changes?: Change[];
    onOpenChange?: (change: Change) => void;
};

const normalizeTabs = (tabs: string[]) => {
    const result: string[] = [];
    let dashboardAdded = false;
    tabs.forEach((tab) => {
        if (tab === 'stats' || tab === 'report' || tab === 'dashboard') {
            if (!dashboardAdded) {
                result.push('dashboard');
                dashboardAdded = true;
            }
            return;
        }
        result.push(tab);
    });
    return result;
};

const resolveContext = (props: ReportingProps): ReportingContext => {
    if (props.sprint)
        return 'sprint';
    if (props.goal)
        return 'goal';
    if (props.project)
        return 'project';
    if (props.client)
        return 'client';
    return 'user';
};

type Filters = {
    user?: User;
    project?: Project;
    client?: Client;
    sprint?: Sprint;
    goal?: Goal;
    from: Moment;
    to: Moment;
    withKm?: boolean;
};

function Reporting(props: ReportingProps) {
    const { t }    = useTranslation();
    const dispatch = useAppDispatch();

    const [filters, setFilters] = useState<Filters>({
        user: props.user,
        project: props.project,
        client: props.client,
        sprint: props.sprint,
        goal: props.goal,
        from: props.from || moment().startOf('day'),
        to: props.to || moment().endOf('day')
    });
    const [workFilter, setWorkFilter] = useState<WorkFilter>('all');

    const tabs = normalizeTabs(props.tabs || ['tracks', 'dashboard']);
    const reportingContext = resolveContext(props);

    const { users }    = useAppSelector((state: RootState) => state.users);
    const loadingUsers = useAppSelector((state: RootState) => state.users.loading);

    const { user }                      = useAppSelector((state: RootState) => state.auth);
    const { clients }                   = useAppSelector((state: RootState) => state.clients);
    const { projects }                  = useAppSelector((state: RootState) => state.projects);
    const { setting }                   = useAppSelector((state: RootState) => state.settings);
    const mileageEnabled                = !!setting?.mileage?.enabled;
    const { report, loadingOne: loadingReport } = useAppSelector((state: RootState) => state.statistics);
    const { tracks, loading } = useAppSelector((state: RootState) => state.tracks);
    const isDashboardLoading = !report
        || loadingReport === 'pending'
        || loading === 'pending';

    const mileageFilterTotals = (() => {
        if (!mileageEnabled || !filters.withKm || !tracks?.length)
            return null;

        const totalKm = tracks.reduce(
            (sum: number, track) => sum + (Number(track.km) || 0),
            0,
        );
        const rate = Number(setting?.mileage?.rate_per_km) || 0;
        const currency = setting?.mileage?.currency || 'CHF';
        const amount = Math.round(totalKm * rate * 100) / 100;
        const formatKm = (n: number) =>
            Number.isInteger(n) ? String(n) : n.toFixed(1);
        const formatAmount = (n: number) =>
            n.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });

        return {
            kmLabel: formatKm(Math.round(totalKm * 10) / 10),
            amountLabel: formatAmount(amount),
            rateLabel: formatAmount(rate),
            currency,
            rate,
        };
    })();

    useEffect(() => {
        if (loadingUsers !== 'pending') {
            dispatch(loadUsers());
            dispatch(loadProjects());
            dispatch(loadClients());
        }
    }, [dispatch]);

    useEffect(() => {
        if (user) {
            dispatch(resetTracks());
            dispatch(loadTracks({
                author: filters.user?._id,
                project: filters.project?._id,
                client: filters.client?._id,
                sprint: filters.sprint?._id,
                goal: filters.goal?._id,
                from: filters.from.toISOString(),
                to: filters.to.toISOString(),
                ...(mileageEnabled && filters.withKm ? { with_km: true } : {}),
            }));
        }
    }, [dispatch, user, filters, mileageEnabled]);

    useEffect(() => {
        if (user) {
            dispatch(resetReport());
            dispatch(getReport({
                user: filters.user?._id,
                project: filters.project?._id,
                client: filters.client?._id,
                sprint: filters.sprint?._id,
                goal: filters.goal?._id,
                from: filters.from.toISOString(),
                to: filters.to.toISOString(),
                work_filter: workFilter === 'all' ? undefined : workFilter,
            }));
        }
    }, [dispatch, user, filters, workFilter]);

    const applyFilters = (value: Interval) => {
        setFilters({
            ...filters,
            from: value?.startDate.startOf('day'),
            to: value?.endDate.endOf('day')
        });
    };

    const onChangedUser = (userId?: string | number) => {
        setFilters({
            ...filters,
            user: users?.find((u: User) => u._id === userId)
        });
    };

    const onChangedProject = (projectId?: string | number) => {
        setFilters({
            ...filters,
            project: projects?.find((p: Project) => p._id === projectId)
        });
    };

    const onChangedClient = (clientId?: string | number) => {
        setFilters({
            ...filters,
            client: clients?.find((p: Client) => p._id === clientId)
        });
    };

    const trackableUsers = React.useMemo(() => {
        const trackable = users?.filter((u: User) => {
            if (filters.sprint && !filters.sprint.resources.map((r: AnyObj) => r.member._id).includes(u._id))
                return false;

            return u._id !== user?._id && [User.ROLE_ADMIN, User.ROLE_USER].includes(u.role);
        }).sort((a: User, b: User) => a.fullname().localeCompare(b.fullname()));
        trackable.unshift(user);

        return trackable;
    }, [users, user]);

    const userOptions = React.useMemo(() => trackableUsers.map((user: User) => ({
        value: user._id,
        label: user.fullname()
    })), [trackableUsers]);

    const projectOptions = React.useMemo(() => projects?.filter((project: Project) => {
        if (!props.goal?.projects)
            return true;

        return props.goal.projects.map((p: Project) => p._id).includes(project._id);
    }).map((project: Project) => ({
        value: project._id,
        label: project.name
    })), [projects]);

    return (
        <div className="Reporting">
            <div className="flex flex-col lg:flex-row justify-center items-center lg:items-start space-y-2 space-x-0 lg:space-x-2 lg:space-y-0">
                {!filters.sprint && !filters.goal && (
                <div className="flex-1 w-full">
                    <Intervalpicker
                        value={{ startDate: filters.from, endDate: filters.to }}
                        inputClassName="w-full rounded-md focus:ring-0 border-gray-900 font-bold bg-blue-100 dark:bg-blue-900/40 dark:bg-blue-900 dark:placeholder:text-blue-100 placeholder:text-sm"
                        toggleClassName="absolute bg-gray-900 border-1 border-gray-400 rounded-r-lg text-white right-0 h-full px-3 text-gray-400 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                        separator={"->"}
                        onChange={applyFilters}
                        maxDate={moment()}
                        direction='down'
                    />
                </div>
                )}
                {user?.can('track:see_other') && (
                    <div className="flex-1 flex w-full">
                        <Select
                            defaultValue={filters.user?._id}
                            options={userOptions}
                            placeholder={t('tracks.select_user') || ''}
                            onChange={onChangedUser}
                            className="!mt-0 !w-full border border-gray-400 rounded-lg rounded-r-none"
                            containerClassName="relative w-full cursor-default rounded-lg bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm shadow-md"
                            selectedClassName="h-[26px] font-bold flex items-center"
                            inline
                        />
                        <div className="flex flex-0 h-[44px] w-[45px] bg-gray-900 shadow-md border border-gray-400 border-l-0 rounded-lg rounded-l-none">
                            <Button
                                title=""
                                icon={filters.user ? 'close' : 'user'}
                                color="none"
                                iconColor="white"
                                className="shadow-none"
                                onClick={() => onChangedUser(undefined)}
                            />
                        </div>
                    </div>
                )}
                {!props.project && (!props.goal || props.goal.projects?.length > 1) && (
                    <div className="flex-1 flex w-full">
                        <Select
                            defaultValue={filters.project?._id}
                            options={projectOptions}
                            placeholder={t('tracks.select_project') || ''}
                            onChange={onChangedProject}
                            className="!mt-0 !w-full border border-gray-400 rounded-lg rounded-r-none"
                            containerClassName="relative w-full cursor-default rounded-lg bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm shadow-md"
                            selectedClassName="h-[26px] font-bold flex items-center"
                            inline
                        />
                        <div className="flex flex-0 h-[44px] w-[45px] bg-gray-900 shadow-md border border-gray-400 border-l-0 rounded-lg rounded-l-none">
                            <Button
                                title=""
                                icon={filters.project ? 'close' : 'project'}
                                color="none"
                                iconColor="white"
                                className="shadow-none"
                                onClick={() => onChangedProject(undefined)}
                            />
                        </div>
                    </div>
                )}
                {!props.goal && !props.client && (
                    <div className="relative z-20 flex flex-1 w-full">
                        <Formik
                            initialValues={filters}
                            enableReinitialize
                            onSubmit={(values, { setSubmitting }) => {}}
                        >
                            <ClientSelectField
                                name="client"
                                onChange={(client?: Client) => { onChangedClient(client?._id) }}
                                className="!mt-0 !w-full !p-0 border border-gray-400 rounded-lg rounded-r-none"
                                selectedClassName="!text-gray-900 dark:text-gray-100 !bg-white dark:bg-gray-800 !text-sm font-bold"
                                searchInputClassName="h-[42px] !ring-0"
                                resultsClassName="!mt-0"
                                value={filters.client}
                            />
                        </Formik>
                        {filters.client?._id && (
                            <Link
                                to={clientPath(filters.client._id)}
                                title={t('client_view.open_fiche') || ''}
                                className="flex h-[44px] w-[45px] items-center justify-center border border-gray-400 border-l-0 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 shadow-md hover:bg-slate-100 dark:hover:bg-gray-700"
                            >
                                <Icon type="eye" size={5} />
                            </Link>
                        )}
                        <div className="flex flex-0 h-[44px] w-[45px] bg-gray-900 shadow-md border border-gray-400 border-l-0 rounded-lg rounded-l-none">
                            <Button
                                title=""
                                icon={filters.client ? 'close' : 'client'}
                                color="none"
                                iconColor="white"
                                className="shadow-none"
                                onClick={() => onChangedClient(undefined)}
                            />
                        </div>
                    </div>
                )}
                {mileageEnabled && (
                    <div className="flex flex-0 w-full lg:w-auto">
                        <Button
                            title={t('tracks.filter_with_km') || ''}
                            color={filters.withKm ? 'primary' : 'default'}
                            className="w-full lg:w-auto h-[44px] shadow-md border border-gray-400"
                            onClick={() => setFilters({ ...filters, withKm: !filters.withKm })}
                        />
                    </div>
                )}
            </div>
            <Tabs className="mt-2">
                {tabs.map((tab: string) => ({
                    'tracks': (
                        <Tabs.Item key={tab} icon={() => <Icon type="list" className="mr-1.5 outline-none" />} title={t('report.tracking')}>
                            {loading === 'pending' ? (
                                <Loader />
                            ) : !tracks?.length ? (
                                <div className="my-4 flex flex-col justify-center items-center">
                                    <Icon type="sad" size={24} color="gray-300" />
                                    <span className="text-gray-400 italic">{t('tracks.nothing_found')}</span>
                                </div>
                            ) : (
                                <>
                                    {mileageFilterTotals && (
                                        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-900 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-100">
                                            <span className="font-bold">
                                                {t('tracks.mileage_filter_total_km', {
                                                    km: mileageFilterTotals.kmLabel,
                                                })}
                                            </span>
                                            <span className="text-indigo-400 dark:text-indigo-500">·</span>
                                            <span className="font-bold">
                                                {t('tracks.mileage_filter_total_amount', {
                                                    amount: mileageFilterTotals.amountLabel,
                                                    currency: mileageFilterTotals.currency,
                                                })}
                                            </span>
                                            {mileageFilterTotals.rate > 0 && (
                                                <span className="text-xs text-indigo-600/80 dark:text-indigo-300/80">
                                                    {t('tracks.mileage_filter_rate_hint', {
                                                        rate: mileageFilterTotals.rateLabel,
                                                        currency: mileageFilterTotals.currency,
                                                    })}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <Tracks tracks={tracks} onOpenChange={props.onOpenChange} />
                                </>
                            )}
                        </Tabs.Item>
                    ),
                    'dashboard': (
                        <Tabs.Item key={tab} icon={() => <Icon type="chart" className="mr-1.5 outline-none" />} title={t('report.dashboard.tab')}>
                            {isDashboardLoading ? (
                                <DashboardSkeleton />
                            ) : (
                                <Dashboard
                                    report={report}
                                    tracks={tracks || []}
                                    context={reportingContext}
                                    workFilter={workFilter}
                                    onWorkFilterChange={setWorkFilter}
                                />
                            )}
                        </Tabs.Item>
                    ),
                    'changes': (
                        <Tabs.Item key={tab} icon={() => <Icon type="feature" className="mr-1.5 outline-none" />} title={t('report.changes_tab')}>
                            <ChangesList
                                // Filter by status and priority
                                changes={props.changes?.sort((a: Change, b: Change) => {
                                    if (a.statusIndex() === b.statusIndex()) {
                                        return b.priority - a.priority; // Inverse the order of priority
                                    }
                                    return a.statusIndex() - b.statusIndex();
                                }) || []}
                                withHeader
                            />
                        </Tabs.Item>
                    )
                }[tab]))}
            </Tabs>
        </div>
    );
}

export default Reporting;
